/**
 * iter164 — Global PDF Preview Dialog.
 *
 * `openPdfPreview(blob, filename)` çağrıldığında açılır; iframe içinde PDF
 * gösterir; sağ üst toolbar: Zoom-Out / Zoom-In / Reset / Fit / Yazdır / İndir / Kapat.
 * Zoom uygulaması: iframe wrapper üzerine CSS transform: scale() → instant zoom,
 * PDF yeniden yüklenmez.
 */
import React from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { subscribePdfPreview } from "../lib/pdfPreview";
import {
  ZoomIn, ZoomOut, Maximize2, RotateCcw, Printer, Download, X, FileText,
} from "lucide-react";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.15;

function ToolbarBtn({ onClick, title, children, testid, tone = "default" }) {
  const cls =
    tone === "primary"
      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
      : tone === "danger"
      ? "hover:bg-red-50 text-red-600"
      : "hover:bg-slate-100 text-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition ${cls}`}
    >
      {children}
    </button>
  );
}

export default function PdfPreviewDialog() {
  const [state, setState] = React.useState(null); // { objectUrl, filename }
  const [zoom, setZoom] = React.useState(1);
  const iframeRef = React.useRef(null);

  React.useEffect(() => {
    return subscribePdfPreview(({ objectUrl, filename }) => {
      setZoom(1);
      setState({ objectUrl, filename });
    });
  }, []);

  const close = React.useCallback(() => {
    if (state?.objectUrl) {
      // Delay revoke so print job (if any) still resolves.
      setTimeout(() => URL.revokeObjectURL(state.objectUrl), 5_000);
    }
    setState(null);
  }, [state]);

  const download = () => {
    if (!state) return;
    const a = document.createElement("a");
    a.href = state.objectUrl;
    a.download = state.filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const print = () => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    try {
      w.focus();
      w.print();
    } catch {
      // Bazı tarayıcılarda cross-origin block olur — yeni sekme fallback.
      window.open(state.objectUrl, "_blank");
    }
  };

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);
  const zoomFit = () => setZoom(0.9);

  if (!state) return null;

  return (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent
        className="p-0 gap-0 max-w-6xl w-[95vw] h-[92vh] flex flex-col overflow-hidden [&>button:last-child]:hidden"
        data-testid="pdf-preview-dialog"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">PDF Önizleme</DialogTitle>
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-indigo-600 shrink-0" />
            <span
              className="text-sm font-medium text-slate-800 truncate"
              data-testid="pdf-preview-filename"
              title={state.filename}
            >
              {state.filename}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            <ToolbarBtn onClick={zoomOut} title="Uzaklaştır" testid="pdf-zoom-out">
              <ZoomOut size={14} />
            </ToolbarBtn>
            <span
              className="text-xs tabular-nums text-slate-600 min-w-[42px] text-center select-none"
              data-testid="pdf-zoom-level"
            >
              {Math.round(zoom * 100)}%
            </span>
            <ToolbarBtn onClick={zoomIn} title="Yakınlaştır" testid="pdf-zoom-in">
              <ZoomIn size={14} />
            </ToolbarBtn>
            <ToolbarBtn onClick={zoomFit} title="Sayfaya Sığdır" testid="pdf-zoom-fit">
              <Maximize2 size={14} />
            </ToolbarBtn>
            <ToolbarBtn onClick={zoomReset} title="Sıfırla (100%)" testid="pdf-zoom-reset">
              <RotateCcw size={14} />
            </ToolbarBtn>
            <div className="w-px h-5 bg-slate-300 mx-1" />
            <ToolbarBtn onClick={print} title="Yazdır" testid="pdf-print" tone="default">
              <Printer size={14} /> Yazdır
            </ToolbarBtn>
            <ToolbarBtn onClick={download} title="İndir" testid="pdf-download" tone="primary">
              <Download size={14} /> İndir
            </ToolbarBtn>
            <ToolbarBtn onClick={close} title="Kapat" testid="pdf-close" tone="danger">
              <X size={14} />
            </ToolbarBtn>
          </div>
        </div>

        {/* PDF viewport with CSS-scale zoom */}
        <div className="flex-1 overflow-auto bg-slate-300" data-testid="pdf-preview-viewport">
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
              width: `${100 / zoom}%`,
              height: `${100 / zoom}%`,
              minHeight: "100%",
            }}
          >
            <iframe
              ref={iframeRef}
              src={state.objectUrl}
              title="PDF Önizleme"
              className="w-full h-full border-0 bg-white"
              data-testid="pdf-preview-iframe"
              style={{ minHeight: "80vh" }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
